/**
 * Types submenu Pemeliharaan (Sinkronisasi COA, Perbaikan Skema DB Transaksi,
 * Sinkronisasi Summary Pajak).
 *
 * Tiga alat sinkronisasi & perbaikan database yang dipakai berulang — dipindahkan dari
 * halaman Migrasi COA 9 Digit (keputusan developer 19-10-2026). Masing-masing SINKRON
 * dengan DTO backend-nya sendiri (lihat komentar per bagian).
 */

// ============================================================
// SINKRONISASI COA — melengkapi tenant dengan akun standar yang belum ada.
// Berbeda dari migrasi: aditif murni, idempoten, tidak menyentuh jurnal.
//
// SINKRON dengan `Booku.Application/DTOs/MigrasiCoa/SinkronisasiCoaDto.cs`.
// Endpoint tetap di `/api/migrasi-coa/sinkronisasi/*` (MigrasiCoaController).
// Rancangan: `.claude/docs/Booku v2/05-migration/fase-5-menu-migrasi-coa.md` §11
// ============================================================

export interface AkunKurang {
  coa: string;
  namaAkun: string;
  kodeMataUang: string;
  dk: string;
}

/** Perbedaan nama akun — dilaporkan saja, TIDAK diubah. */
export interface BedaNama {
  coa: string;
  namaDiTenant: string;
  namaStandar: string;
}

export interface PratinjauSinkronisasi {
  boleh: boolean;
  alasan?: string | null;
  jumlahStandar: number;
  jumlahDiTenant: number;
  tahunBuku: number[];
  akanDisisipkan: AkunKurang[];
  barisSaldoAkanDisisipkan: number;
  coaKustom: string[];
  bedaNama: BedaNama[];
}

export interface HasilSinkronisasi {
  berhasil: boolean;
  pesan: string;
  coaDisisipkan: number;
  barisSaldoDisisipkan: number;
  rincian: string[];
}

// ============================================================
// PERBAIKAN SKEMA DB TRANSAKSI — membuat tabel yang hilang, menambah kolom yang hilang
// (31-10-2026) & melebarkan kolom varchar yang sempit (Lapis 2, 20-10-2026) di tiap tahun buku.
//
// Endpoint-nya milik controller tersendiri (`/api/skema-db-transaksi`).
//
// Bentuk DTO-nya SAMA untuk pratinjau maupun hasil — bedanya hanya makna
// `tabel`: daftar yang HILANG saat pratinjau, daftar yang BARU DIBUAT
// setelah perbaikan.
//
// Rancangan: `.claude/docs/Booku v2/05-migration/perbaikan-skema-db-transaksi.md`
// ============================================================

/**
 * Kolom varchar yang lebih sempit di DB daripada DDL Provisioning (Lapis 2 penjaga lebar
 * kolom, keputusan developer 20-10-2026). Saat dilebarkan, definisi kolom diambil PENUH dari
 * DDL (termasuk NOT NULL & DEFAULT).
 */
export interface KolomSempit {
  /** Nama tabel sebagaimana di DB (bisa Mixed_Case). */
  tabel: string;
  kolom: string;
  lebarSekarang: number;
  lebarDdl: number;
  /** Baris yang panjang nilainya tepat selebar kolom sekarang — diduga sudah terpotong. */
  dugaanTerpotong: number;
  /** Baris NULL yang akan menjadi string kosong (DB nullable, DDL NOT NULL). */
  nilaiNull: number;
}

/** Kolom DDL yang tidak ada di tabel DB (keputusan developer 31-10-2026 — temuan simulasi #29). */
export interface KolomHilang {
  tabel: string;
  kolom: string;
  /** Definisi penuh dari DDL, mis. `bigint(27) NOT NULL`. */
  definisi: string;
  /** Isi tabel — bukan nol ⇒ `ditangguhkan`. */
  jumlahBaris: number;
  /**
   * Tabel berisi data ⇒ kolom TIDAK ditambah, tabelnya tidak diubah (Opsi A, 31-10-2026): butuh aturan
   * pengisian per kolom & bisa jadi kolom yang berganti nama.
   */
  ditangguhkan: boolean;
}

/** Kolom DB yang tidak dikenal DDL — tidak pernah dihapus. */
export interface KolomAsing {
  tabel: string;
  kolom: string;
  tipe: string;
  /** NOT NULL tanpa DEFAULT — simpan V2 ditolak; Perbaiki menjadikannya boleh NULL. */
  wajibTanpaDefault: boolean;
  /** Tabelnya ditangguhkan — tidak diubah sama sekali. */
  ditangguhkan: boolean;
}

export interface SkemaTahunBuku {
  tahunBuku: number;
  /** Nama DB transaksi (`bookuid_booku_{tenant}_{tahun}`). */
  namaDatabase: string;
  /** Pratinjau: tabel yang HILANG. Hasil: tabel yang BARU DIBUAT. */
  tabel: string[];
  /** Pratinjau: kolom yang SEMPIT. Hasil: kolom yang BERHASIL dilebarkan. */
  kolom: KolomSempit[];
  /** Kolom yang di DDL varchar tetapi di DB bertipe lain — dilaporkan saja. */
  kolomTipeBerbeda: string[];
  /** Pratinjau: kolom yang HILANG. Hasil: kolom yang BERHASIL ditambah + yang `ditangguhkan` (tetap hilang). */
  kolomHilang: KolomHilang[];
  /** Pratinjau: seluruh kolom asing. Hasil: kolom asing yang BERHASIL dijadikan boleh NULL. */
  kolomAsing: KolomAsing[];
  /** Galat pelebaran / penambahan kolom per tabel (`tabel: pesan`). */
  galatKolom: string[];
  /** Terisi bila DB tahun ini gagal diperiksa — tahun lain tetap diproses. */
  galat?: string | null;
}

export interface StatusSkemaDbTransaksi {
  tahun: SkemaTahunBuku[];
  /** Total tabel hilang (pratinjau) atau total tabel dibuat (hasil). */
  totalTabel: number;
  /** Total kolom sempit (pratinjau) atau total kolom dilebarkan (hasil). */
  totalKolom: number;
  /** Total baris yang diduga sudah terpotong (hanya dilaporkan). */
  totalDugaanTerpotong: number;
  /** Total kolom hilang yang akan/sudah ditambah — TANPA kolom di tabel yang ditangguhkan. */
  totalKolomHilang: number;
  /** Total kolom hilang di tabel berisi data — dilaporkan saja, tidak ditambah. */
  totalKolomDitangguhkan: number;
  /** Total kolom asing NOT NULL tanpa DEFAULT di tabel yang tidak ditangguhkan (pratinjau) atau yang dijadikan boleh NULL (hasil). */
  totalKolomAsingDiubah: number;
  /** True bila ADA tahun/tabel yang gagal — rinciannya di `tahun[].galat` & `tahun[].galatKolom`. */
  adaGalat: boolean;
  pesan: string;
}

// ============================================================
// SINKRONISASI SUMMARY PAJAK — menyelaraskan baris summary hutang pajak
// (ADR-006) di setiap tahun buku NORMAL dengan langkah hitung transfer
// Tutup Buku yang sekarang.
//
// Endpoint-nya milik controller tersendiri (`/api/sinkronisasi-summary-pajak`).
//
// Rancangan: `.claude/docs/Booku v2/buku-pengawasan/pajak/tahun-pajak-lama.md` §4.3
// ============================================================

export interface SinkronisasiSummaryPajakBaris {
  /** Jenis_Pajak Grup A, mis. "PPh Pasal 23". */
  jenisPajak: string;
  /** Kosong untuk PPh 25, "Non" untuk PPN. */
  kodeSetoran: string;
  tahunPajak: number;
  /** Σ baris summary yang kini tersimpan (0 bila belum ada). */
  tersimpan: number;
  /** Σ menurut langkah hitung transfer sekarang (0 = seharusnya tidak ada baris). */
  seharusnya: number;
  selisih: number;
}

export interface SinkronisasiSummaryPajakTahun {
  tahunBuku: number;
  /** OPEN / CLOSED — tahun CLOSED tetap diselaraskan (data List, bukan jurnal). */
  statusBuku: string;
  /** Kunci yang berbeda saja. Kosong = sudah selaras. */
  baris: SinkronisasiSummaryPajakBaris[];
}

export interface SinkronisasiSummaryPajak {
  /** False = pratinjau; true = hasil penjalanan. */
  dijalankan: boolean;
  /** Setiap tahun buku NORMAL, urut menaik. */
  perTahun: SinkronisasiSummaryPajakTahun[];
  jumlahBarisBerbeda: number;
  sudahSelaras: boolean;
  /** Pratinjau saja: angka tahun setelah tahun pertama yang berbeda bisa bergeser saat dijalankan. */
  angkaTahunBerikutBisaBergeser: boolean;
}

// ============================================================================
// Migrasi Reimbursement (ADR-013, eks "Deposit Operasional") — MigrasiReimbursementController
// ============================================================================

/** Rute halaman alat — dirujuk penghalang Tutup Buku Tahunan (ADR-013 D15). Sama dengan PageRegistry. */
export const MigrasiReimbursement_Path = '/pemeliharaan/migrasi-reimbursement';

/**
 * Keadaan satu DB tahun buku. Pada pratinjau angka = baris yang AKAN diubah;
 * pada hasil Jalankan = baris yang SUDAH diubah.
 */
export interface MigrasiReimbursementTahun {
  tahunBuku: number;
  namaDatabase: string;
  adaTabelLama: boolean;
  adaTabelBaru: boolean;
  /** Baris tbl_reimbursement yang sudah ada — bukan nol saat tabel lama juga ada ⇒ konflik. */
  barisTabelBaru: number;
  barisModul: number;
  gantiNamaTabel: boolean;
  /** tbl_reimbursement KOSONG buatan Perbaikan Skema yang dibuang sebelum ganti nama. */
  buangTabelBaruKosong: boolean;
  kolomDiganti: number;
  buktiPengeluaran: number;
  buktiPenerimaan: number;
  jenisJurnal: number;
  /** Nilai berawalan BPDO- yang diganti BPRM- (seluruh kolom kunci). */
  nomor: number;
  cadangan?: string | null;
  /** Cadangan sudah ada dari jalan sebelumnya — sengaja tidak ditimpa. */
  cadanganSudahAda: boolean;
  galat?: string | null;
}

export interface StatusMigrasiReimbursement {
  tahun: MigrasiReimbursementTahun[];
  perluMigrasi: boolean;
  dijalankan: boolean;
  adaGalat: boolean;
  /** Akun Piutang/Hutang Reimbursement tersembunyi yang akan/sudah ditampakkan (temuan simulasi #28). */
  akunDitampakkan: number;
  pesan: string;
}
